/* Enhance the existing submit button; never clone the form or override stock rules. */
(() => {
  const mounted = new WeakSet();
  // PDP swipe restoration is independent of the other mobile isolation guards.
  const mobileBaseline = () => window.semboniaMobileBaseline?.pdpSwipeDisabled === true;
  function mobilePanelPosition(current, slotTop, viewportHeight, panelHeight) {
    const boundary = viewportHeight - panelHeight;
    // A small dead band prevents subpixel/viewport changes reversing the handoff.
    if (current === 'inline') return slotTop > boundary + 12 ? 'fixed' : 'inline';
    return slotTop < boundary - 2 ? 'inline' : 'fixed';
  }
  function compareSizes(a, b) {
    const rank = value => {
      const text = value.trim().toUpperCase().replace(/\s+/g, '');
      const basic = {S:0,M:1,L:2};
      if (text in basic) return basic[text];
      const towels = {FACETOWEL:0,HANDTOWEL:1,BATHTOWEL:2};
      if (text in towels) return towels[text];
      const extended = text.match(/^(X+|\d+X)(S|L)$/);
      if (!extended) return null;
      const count = /^\d/.test(extended[1]) ? parseInt(extended[1],10) : extended[1].length;
      return extended[2] === 'S' ? -count : 2 + count;
    };
    const left = rank(a), right = rank(b);
    if (left !== null && right !== null) return left - right;
    if (left !== null) return -1;
    if (right !== null) return 1;
    return a.localeCompare(b, undefined, {numeric:true,sensitivity:'base'});
  }
  function sortSizeOptions(root) {
    root.querySelectorAll('variant-swatch-king fieldset').forEach(row => {
      const name = row.querySelector('.swatch-label .swatch-option-name')?.textContent.trim().replace(/:$/, '').trim();
      if (!/^(size|saiz)$/i.test(name || '')) return;
      const list = row.querySelector('.swatch-view');
      if (!list) return;
      const items = [...list.children].filter(item => item.classList.contains('swatch-view-item'));
      const value = item => item.getAttribute('orig-value') || item.getAttribute('aria-label')?.replace(/\s*-\s*Sold Out$/i,'') || item.textContent.trim();
      const sorted = [...items].sort((a,b) => compareSizes(value(a),value(b)));
      if (items.every((item,index) => item === sorted[index])) return;
      const focused = document.activeElement;
      // Move existing option nodes, retaining variant IDs, stock state and handlers.
      sorted.forEach(item => list.append(item));
      if (focused && list.contains(focused)) focused.focus({preventScroll:true});
    });
  }
  function sentenceCaseQuestion(text) {
    if (text !== text.toUpperCase()) return text;
    return text.toLowerCase()
      .replace(/[a-z]/, letter => letter.toUpperCase())
      .replace(/\bi\b/g, 'I')
      .replace(/\bsembonia\b/g, 'SEMBONIA')
      .replace(/\bmalaysia\b/g, 'Malaysia')
      .replace(/\bp\.o\./g, 'P.O.')
      .replace(/\b(myr|rm|fpx|sku|id|faq)\b/g, word => word.toUpperCase());
  }
  function normalizeDetailsDividers(root) {
    root.querySelectorAll('dialog[id^="ProductDetails-"] .sembonia-terms-drawer__body').forEach(body => {
      const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT);
      const separators = [];
      while (walker.nextNode()) {
        const node = walker.currentNode;
        // Imported separator-only text wraps; never alter ordinary product copy.
        if (/^[\s\u200B]*[─━_—-]{5,}[\s\u200B]*$/u.test(node.nodeValue)) separators.push(node);
      }
      separators.forEach(node => {
        const divider = document.createElement('span');
        divider.className = 'sembonia-product-details-divider';
        divider.setAttribute('role', 'separator');
        node.replaceWith(divider);
      });
    });
  }
  function mount(root) {
    if (mounted.has(root)) return;
    mounted.add(root);
    normalizeDetailsDividers(root);
    const gallery = root.querySelector('.product__media-list');
    const mediaWrapper = root.querySelector('.product__media-wrapper');
    const mediaSpace = document.createElement('div');
    mediaSpace.className = 'sembonia-mobile-media-space';
    mediaSpace.setAttribute('aria-hidden', 'true');
    mediaWrapper?.before(mediaSpace);
    let dotNav, dotItems = [], galleryArrows = [];
    if (gallery && mediaWrapper) {
      dotNav = document.createElement('nav');
      dotNav.className = 'sembonia-gallery-dots';
      dotNav.setAttribute('aria-label', 'Product images');
      mediaWrapper.append(dotNav);
      galleryArrows = [-1, 1].map(step => {
        const arrow = document.createElement('button');
        arrow.type = 'button';
        arrow.className = `sembonia-gallery-arrow sembonia-gallery-arrow--${step < 0 ? 'prev' : 'next'}`;
        arrow.setAttribute('aria-label', step < 0 ? 'Previous image' : 'Next image');
        arrow.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="${step < 0 ? '15,18 9,12 15,6' : '9,18 15,12 9,6'}"/></svg>`;
        arrow.addEventListener('click', () => {
          const dots = Array.from(dotNav.children);
          if (dots.length < 2) return;
          const current = Math.max(0, dots.findIndex(dot => dot.getAttribute('aria-current') === 'true'));
          dots[(current + step + dots.length) % dots.length].click();
        });
        mediaWrapper.append(arrow);
        return arrow;
      });
    }
    const dialogs = Array.from(root.querySelectorAll('.sembonia-terms-drawer'));
    const dialogBaselineCleanups = [];
    for (const termsDialog of dialogs) {
      const termsButton = root.querySelector(`[aria-controls="${termsDialog.id}"]`);
      if (!termsButton) continue;
      // Group existing section content without copying or changing its wording.
      // Native details preserves keyboard support and starts closed by default.
      const termsContent = termsDialog.querySelector('.purchasing-term-content');
      if (termsContent && termsDialog.id.startsWith('PurchasingTerms-')) {
        termsContent.querySelectorAll('h3').forEach(heading => {
          const original = heading.textContent;
          const formatted = sentenceCaseQuestion(original);
          if (formatted === original) return;
          // Update text nodes only, retaining any nested markup and links.
          const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
          let offset = 0;
          while (walker.nextNode()) {
            const node = walker.currentNode;
            const length = node.textContent.length;
            node.textContent = formatted.slice(offset, offset + length);
            offset += length;
          }
        });
      }
      if (termsContent && !termsContent.querySelector('.sembonia-drawer-accordion')) {
        let sectionBody;
        for (const node of Array.from(termsContent.childNodes)) {
          if (node.nodeType === 1 && node.tagName === 'H2') {
            const section = document.createElement('details');
            section.className = 'sembonia-drawer-accordion';
            const summary = document.createElement('summary');
            summary.textContent = node.textContent;
            sectionBody = document.createElement('div');
            sectionBody.className = 'sembonia-drawer-accordion__content';
            section.append(summary, sectionBody);
            node.replaceWith(section);
          } else if (sectionBody) {
            sectionBody.append(node);
          }
        }
      }
      // Move outside the sticky/animated ancestors. Native dialog supplies focus trapping and Escape.
      document.body.append(termsDialog);
      termsDialog.setAttribute('data-lenis-prevent', '');
      let drawerCloseTimer, mobileDialog = false, drawerWasOpen = false;
      const finishDrawerClose = () => {
        if (termsDialog.open || !drawerWasOpen) return;
        drawerWasOpen = false;
        clearTimeout(drawerCloseTimer);
        drawerCloseTimer = null;
        termsDialog.classList.remove('is-closing');
        if (mobileDialog) removeTrapFocus();
        mobileDialog = false;
        window.semboniaScrollLock.release(termsDialog);
        if (!window.semboniaScrollLock.size && termsButton.isConnected) termsButton.focus({preventScroll:true});
      };
      const showDrawer = () => {
        mobileDialog = window.semboniaMobileBaseline?.scrollLockDisabled === true;
        if (mobileDialog) {
          termsDialog.show();
          trapFocus(termsDialog, termsDialog.querySelector('[data-terms-close]'));
        } else termsDialog.showModal();
      };
      const closeDrawer = () => {
        if (!termsDialog.open || (drawerCloseTimer && !window.semboniaMobileBaseline?.instantDrawers)) return;
        if (window.semboniaMobileBaseline?.instantDrawers) {
          clearTimeout(drawerCloseTimer);
          termsDialog.close();
          finishDrawerClose();
          return;
        }
        termsDialog.style.setProperty('--drawer-exit-translate', getComputedStyle(termsDialog).translate);
        termsDialog.classList.add('is-closing');
        drawerCloseTimer = setTimeout(() => {
          drawerCloseTimer = null;
          termsDialog.close();
        }, 500);
      };
      termsDialog.addEventListener('cancel', event => { event.preventDefault(); closeDrawer(); });
      termsDialog.addEventListener('keydown', event => {
        if (mobileDialog && event.key === 'Escape') { event.preventDefault(); closeDrawer(); }
      });
      termsButton.addEventListener('click', () => {
        if (termsDialog.open) return;
        window.semboniaScrollLock.acquire(termsDialog);
        try {
          drawerWasOpen = true;
          showDrawer();
        } catch (error) {
          drawerWasOpen = false;
          window.semboniaScrollLock.release(termsDialog);
          throw error;
        }
      });
      termsDialog.querySelector('[data-terms-close]').addEventListener('click', closeDrawer);
      termsDialog.addEventListener('click', event => {
        if (event.target !== termsDialog) return;
        const rect = termsDialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDrawer();
      });
      termsDialog.addEventListener('close', finishDrawerClose);
      const syncDrawerBaseline = () => {
        if (!termsDialog.open) return;
        if (drawerCloseTimer && window.semboniaMobileBaseline?.instantDrawers) { closeDrawer(); return; }
        if (mobileDialog === (window.semboniaMobileBaseline?.scrollLockDisabled === true)) return;
        if (mobileDialog) removeTrapFocus();
        termsDialog.close();
        showDrawer();
      };
      window.addEventListener('sembonia:mobile-baseline-change', syncDrawerBaseline);
      dialogBaselineCleanups.push(() => window.removeEventListener('sembonia:mobile-baseline-change', syncDrawerBaseline));
    }
    let frame, secondary, mobilePanel, panelSlot, reveal, panelOptions, optionsSignature, panelHeight = 0;
    const info = root.querySelector('.product__info-container');
    const infoWrapper = root.querySelector('.product__info-wrapper');
    let settleTimer, snapTimer, snapFrame, snapping = false, gesture = null;
    let transitionActive = false;
    let panelMotion = null, resumePanelTarget = null;
    let panelAnimation = null;
    const panelDuration = 600;
    let contentReturnTarget = null, resumeContentTarget = null;
    // Overdamped spring: mass=1, stiffness=300, damping=60. Normalize its
    // response to finish exactly at 600ms, using the same curve both ways.
    function panelSpring(progress) {
      const mass = 1, stiffness = 300, damping = 60;
      const discriminant = Math.sqrt(damping * damping - 4 * mass * stiffness);
      const slow = (-damping + discriminant) / (2 * mass);
      const fast = (-damping - discriminant) / (2 * mass);
      const response = t => 1 - (fast * Math.exp(slow * t) - slow * Math.exp(fast * t)) / (fast - slow);
      const duration = panelDuration / 1000;
      return response(Math.max(0, Math.min(1, progress)) * duration) / response(duration);
    }
    const panelLocked = () => window.semboniaScrollLock?.busy || window.semboniaScrollLock?.size;
    const panelScrollTarget = target => root.contains(target) || !!target.closest('main,footer,.shopify-section-group-footer-group,.section-header');
    function panelStops() {
      if (panelMotion) return panelMotion.stops;
      const header = parseFloat(getComputedStyle(root).getPropertyValue('--sembonia-header-height')) || 64;
      return {
        collapsed:Math.max(0, window.scrollY + mediaSpace.getBoundingClientRect().top - header),
        expanded:Math.max(0, window.scrollY + infoWrapper.getBoundingClientRect().top - header)
      };
    }
    function writePanelScroll(top) {
      const lenis = window.semboniaLenis;
      if (lenis && !lenis.isStopped) lenis.scrollTo(top, {immediate:true});
      else window.scrollTo({top, behavior:'instant'});
      schedule();
    }
    function cancelPanelMotion() {
      clearTimeout(settleTimer); clearTimeout(snapTimer); cancelAnimationFrame(snapFrame);
      if (contentReturnTarget !== null) window.scrollTo({top:window.scrollY, behavior:'instant'});
      contentReturnTarget = null;
      delete root.dataset.contentReturning;
      if (panelAnimation) {
        const {animation, from, to} = panelAnimation;
        const progress = Math.min(1, Math.max(0, Number(animation.currentTime || 0) / panelDuration));
        if (panelMotion) renderPanelMotion(from + (to - from) * panelSpring(progress));
        animation.onfinish = null;
        animation.cancel();
        panelAnimation.followers.forEach(item => item.cancel());
        panelAnimation = null;
      }
      snapping = false;
    }
    function startPanelMotion() {
      if (panelMotion) return;
      const stops = panelStops();
      // Keep Shopify sections in their existing DOM locations (and preserve
      // their handlers), but translate every following section with the sheet.
      const followers = [];
      const section = root.closest('.shopify-section');
      if (section) {
        for (let next = section.nextElementSibling; next; next = next.nextElementSibling) {
          if (next.matches('.shopify-section')) followers.push(next);
        }
        document.querySelectorAll('.shopify-section-group-footer-group').forEach(node => {
          if (!followers.some(parent => parent === node || parent.contains(node))) followers.push(node);
        });
      }
      panelMotion = {stops, base:window.scrollY, current:window.scrollY, followers};
      followers.forEach(node => node.classList.add('sembonia-pdp-panel-follower'));
      transitionActive = true;
      // One in-flow information layer for the whole journey. Never switch its
      // positioning, gallery height or option rows on individual animation frames.
      root.dataset.panelPosition = 'inline';
      root.dataset.panelMoving = 'true';
      root.dataset.galleryCovered = 'false';
      secondary.hidden = true;
      writePanelScroll(window.scrollY); // Cancel pending wheel inertia once.
    }
    function renderPanelMotion(top) {
      panelMotion.current = top;
      infoWrapper.style.setProperty('--pdp-panel-translate', `${panelMotion.base - top}px`);
      panelMotion.followers.forEach(node => node.style.setProperty('--pdp-panel-translate', `${panelMotion.base - top}px`));
    }
    function clearPanelLayer() {
      infoWrapper.style.removeProperty('--pdp-panel-translate');
      panelMotion?.followers.forEach(node => {
        node.style.removeProperty('--pdp-panel-translate');
        node.classList.remove('sembonia-pdp-panel-follower');
      });
      delete root.dataset.panelMoving;
      panelMotion = null;
      transitionActive = false;
    }
    function onPanelRestored() {
      if (resumeContentTarget !== null) {
        const target = resumeContentTarget; resumeContentTarget = null;
        returnContentToInformation(target);
      } else if (resumePanelTarget !== null) {
        const target = resumePanelTarget; resumePanelTarget = null;
        movePanelTo(target, 0);
      } else schedule();
    }
    function onPanelLocked() {
      // A drawer may interrupt touchend or a settling wheel gesture. Do not
      // leave an active panel layer intercepting the next swipe after unlock.
      if (contentReturnTarget !== null) resumeContentTarget = contentReturnTarget;
      if (panelMotion) {
        resumePanelTarget = panelMotion.target ?? (gesture?.expanded ? panelMotion.stops.expanded : panelMotion.stops.collapsed);
      }
      cancelPanelMotion();
      gesture = null;
    }
    function returnContentToInformation(top) {
      if (window.innerWidth >= 810 || panelLocked()) return;
      if (mobileBaseline()) {
        cancelPanelMotion(); clearPanelLayer(); gesture = null;
        writePanelScroll(top);
        return;
      }
      cancelPanelMotion();
      const from = window.scrollY;
      contentReturnTarget = top;
      root.dataset.contentReturning = 'true';
      snapping = true;
      writePanelScroll(from);
      // Let the browser own document motion, as it does for native touch scroll.
      // No fixed duration or panel spring is applied to a full-page return.
      window.scrollTo({top, behavior:'smooth'});
      const tick = () => {
        if (panelLocked()) {
          resumeContentTarget = top; cancelPanelMotion(); return;
        }
        if (window.innerWidth >= 810) { cancelPanelMotion(); return; }
        if (Math.abs(window.scrollY - top) > 1) snapFrame = requestAnimationFrame(tick);
        else {
          writePanelScroll(top); // Synchronize Lenis once after native settling.
          contentReturnTarget = null; delete root.dataset.contentReturning; snapping = false; schedule();
        }
      };
      snapFrame = requestAnimationFrame(tick);
    }
    function movePanelTo(top, delay = 0) {
      if (window.innerWidth >= 810 || panelLocked()) return;
      if (mobileBaseline()) {
        cancelPanelMotion(); clearPanelLayer(); gesture = null;
        writePanelScroll(top);
        update();
        return;
      }
      cancelPanelMotion();
      startPanelMotion();
      panelMotion.target = top;
      const from = panelMotion.current;
      snapping = true;
      const complete = () => {
        // Transfer the final transform into document scroll in the same frame.
        // The browser never paints a half-fixed/half-inline intermediate state.
        writePanelScroll(top);
        if (panelAnimation) {
          panelAnimation.animation.onfinish = null;
          panelAnimation.animation.cancel();
          panelAnimation.followers.forEach(item => item.cancel());
          panelAnimation = null;
        }
        clearPanelLayer();
        snapping = false;
        update();
      };
      // Explicitly restored panel motion must use the same visible travel both ways.
      snapTimer = setTimeout(() => {
        if (panelLocked()) { resumePanelTarget = top; cancelPanelMotion(); return; }
        if (window.innerWidth >= 810) { cancelPanelMotion(); clearPanelLayer(); schedule(); return; }
        if (typeof infoWrapper.animate === 'function') {
          const keyframes = Array.from({length:61}, (_, index) => {
            const progress = index / 60;
            const position = from + (top - from) * panelSpring(progress);
            return {offset:progress, transform:`translate3d(0,${panelMotion.base - position}px,0)`};
          });
          const animation = infoWrapper.animate(keyframes, {duration:panelDuration, easing:'linear', fill:'forwards'});
          const followers = panelMotion.followers.map(node => node.animate(keyframes, {duration:panelDuration, easing:'linear', fill:'forwards'}));
          // All layers use one timeline origin, not independent completion times.
          const startTime = document.timeline?.currentTime;
          if (typeof startTime === 'number') [animation, ...followers].forEach(item => { item.startTime = startTime; });
          panelAnimation = {animation, followers, from, to:top};
          animation.onfinish = complete;
          return;
        }
        let started;
        const tick = time => {
          if (panelLocked()) {
            resumePanelTarget = top;
            cancelPanelMotion(); gesture = null; return;
          }
          if (window.innerWidth >= 810) {
            cancelPanelMotion(); clearPanelLayer(); gesture = null; schedule(); return;
          }
          if (started === undefined) started = time;
          const progress = Math.min(1, (time - started) / panelDuration);
          renderPanelMotion(from + (top - from) * panelSpring(progress));
          if (progress < 1) snapFrame = requestAnimationFrame(tick);
          else complete();
        };
        snapFrame = requestAnimationFrame(tick);
      }, delay);
    }
    function beginPanelGesture(target) {
      if (mobileBaseline() || window.innerWidth >= 810 || !mobilePanel || panelLocked() ||
          !panelScrollTarget(target) || target.closest('input,select,textarea,[contenteditable="true"]')) return null;
      const stops = panelStops();
      if (window.scrollY < stops.collapsed - 2) return null;
      return {stops, origin:window.scrollY, distance:0, active:false,
        expanded:root.dataset.panelPosition === 'inline'};
    }
    function dragPanel(distance) {
      if (!gesture) return;
      const {stops, expanded} = gesture;
      let origin = gesture.origin;
      if (!gesture.active) {
        if ((!expanded && distance <= 0) || (expanded && distance >= 0)) return;
        // Returning from deeper information stops beneath the navbar for this
        // gesture. A fresh gesture then restores the compact panel and image.
        if (origin > stops.expanded + 2) {
          if (origin + distance > stops.expanded) return;
          gesture.entryDistance = stops.expanded - origin;
          gesture.returnedFromContent = true;
          gesture.origin = origin = stops.expanded;
          writePanelScroll(stops.expanded);
        }
        cancelPanelMotion();
        gesture.active = true;
        startPanelMotion();
      }
      gesture.distance = distance;
      const travelDistance = distance - (gesture.entryDistance || 0);
      renderPanelMotion(gesture.returnedFromContent ? stops.expanded : Math.max(stops.collapsed, Math.min(stops.expanded, origin + travelDistance)));
    }
    function finishPanelGesture(cancelled = false, delay = 0) {
      if (!gesture) return;
      const current = gesture; gesture = null;
      if (current.touchMode === 'return-content') {
        if (!cancelled && current.distance < 0) returnContentToInformation(current.stops.expanded);
        return;
      }
      if (!current.active) return;
      if (current.returnedFromContent) {
        movePanelTo(current.stops.expanded, delay);
        return;
      }
      const distance = current.distance - (current.entryDistance || 0);
      const directedDistance = current.expanded ? -distance : distance;
      // A completed directional swipe is sufficient, regardless of panel height.
      // Keep cancelled/reversed gestures at their starting endpoint.
      const passed = !cancelled && directedDistance > 0;
      const expanded = passed ? !current.expanded : current.expanded;
      movePanelTo(expanded ? current.stops.expanded : current.stops.collapsed, delay);
    }
    function onPanelScroll() { schedule(); }
    function onPanelTouchStart(event) {
      if (mobileBaseline()) { gesture = null; return; }
      if (panelLocked()) { gesture = null; return; }
      if (contentReturnTarget !== null) cancelPanelMotion();
      if (gesture?.active) finishPanelGesture(true);
      if (event.touches.length !== 1) { finishPanelGesture(true); return; }
      gesture = beginPanelGesture(event.target);
      if (gesture) { gesture.x = event.touches[0].clientX; gesture.y = event.touches[0].clientY; }
    }
    function onPanelTouchMove(event) {
      if (mobileBaseline()) return;
      if (panelLocked()) return;
      if (snapping && panelScrollTarget(event.target) && event.cancelable && event.touches.length === 1) {
        event.preventDefault(); event.stopImmediatePropagation(); return;
      }
      if (!gesture || event.touches.length !== 1) return;
      const dx = event.touches[0].clientX - gesture.x;
      const dy = gesture.y - event.touches[0].clientY;
      // Decide on the first movement, before the browser commits the gesture to
      // native scrolling. Taking over only at the boundary is too late on touch.
      if (!gesture.active && !gesture.touchMode) {
        if (!dx && !dy) return;
        if (Math.abs(dx) > Math.abs(dy)) { gesture = null; return; }
        if (gesture.expanded && dy > 0) {
          // The information, recommendations and footer must remain freely
          // scrollable. Do not keep a dormant panel gesture for this swipe.
          gesture = null;
          return;
        }
        if (!gesture.expanded && dy < 0) { gesture = null; return; }
        gesture.touchMode = gesture.origin > gesture.stops.expanded + 2 ? 'return-content' : 'panel';
      }
      if (!event.cancelable) {
        // Never leave the moving layer armed after native scrolling takes over.
        if (gesture.active) finishPanelGesture(true);
        else gesture = null;
        return;
      }
      if (gesture.touchMode === 'return-content') {
        event.preventDefault(); event.stopImmediatePropagation();
        // Claim the whole return gesture, not just its last events. No native
        // momentum can carry this first swipe past the information's top stop.
        gesture.distance = dy;
        writePanelScroll(Math.max(gesture.stops.expanded, gesture.origin + dy));
        return;
      }
      dragPanel(dy);
      if (gesture?.active) { event.preventDefault(); event.stopImmediatePropagation(); }
    }
    function onPanelTouchEnd(event) {
      if (mobileBaseline()) return;
      // The panel owns the release as well as the moves it consumed. Do not
      // let Lenis start a second inertia animation over the panel's settling.
      if (gesture?.active || gesture?.touchMode || snapping) event?.stopImmediatePropagation();
      finishPanelGesture();
    }
    function onPanelTouchCancel(event) {
      if (mobileBaseline()) return;
      if (gesture?.active || gesture?.touchMode || snapping) event?.stopImmediatePropagation();
      finishPanelGesture(true);
    }
    function onPanelWheel(event) {
      if (mobileBaseline()) return;
      if (panelLocked()) return;
      if (event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      if (snapping && panelScrollTarget(event.target) && event.cancelable) {
        event.preventDefault(); event.stopImmediatePropagation(); return;
      }
      if (!gesture) gesture = beginPanelGesture(event.target);
      if (!gesture) return;
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
      gesture.wheelDistance = (gesture.wheelDistance || 0) + event.deltaY * unit;
      dragPanel(gesture.wheelDistance);
      if (gesture?.active) {
        event.preventDefault(); event.stopImmediatePropagation();
      }
      // Retain the gesture's original content position across its wheel events,
      // including native scrolling, so its first return cannot skip the top stop.
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => finishPanelGesture(false, 0), 100);
    }
    const summaryNodes = [root.querySelector('.product__info-container > .product__title')].filter(Boolean);
    function panelOptionSource() {
      const rows = [...info.querySelectorAll('variant-swatch-king fieldset')];
      const name = row => row.querySelector('.swatch-label .swatch-option-name')?.textContent.trim().replace(/:$/, '').trim() || '';
      return rows.find(row => /^colou?r$/i.test(name(row))) || rows.find(row => /^(size|saiz)$/i.test(name(row)));
    }
    function hasColourAndSize() {
      const names = [...info.querySelectorAll('variant-swatch-king .swatch-option-name, variant-swatch-king .swatch-label input[name]')]
        .map(node => (node.getAttribute?.('name') || node.textContent || '').trim().replace(/:$/, '').trim());
      return names.some(name => /^colou?r$/i.test(name)) && names.some(name => /^(size|saiz)$/i.test(name));
    }
    function revealSizeOptions() {
      const header = parseFloat(getComputedStyle(root).getPropertyValue('--sembonia-header-height')) || 64;
      movePanelTo(Math.max(0, window.scrollY + infoWrapper.getBoundingClientRect().top - header));
    }
    function syncPanelOptions() {
      const row = panelOptionSource();
      panelOptions.dataset.option = /^colou?r\s*:?$/i.test(row?.querySelector('.swatch-label .swatch-option-name')?.textContent.trim() || '') ? 'colour' : 'size';
      const items = row ? [...row.querySelectorAll('.swatch-view-item')] : [];
      const label = row?.querySelector('.swatch-label')?.textContent.trim().replace(/\s+/g, ' ') || '';
      const data = items.map(item => ({
        label: item.getAttribute('aria-label') || item.getAttribute('orig-value') || item.textContent.trim(),
        selected: item.getAttribute('aria-checked') === 'true',
        disabled: item.getAttribute('aria-disabled') === 'true',
        soldOut: item.classList.contains('swatch-item-unavailable') || /sold out/i.test(item.getAttribute('aria-label') || ''),
        image: item.querySelector('.star-set-image') ? getComputedStyle(item.querySelector('.star-set-image')).backgroundImage : '',
        stockLine: item.querySelector('.star-set-image') && item.classList.contains('swatch-item-unavailable')
          ? (() => { const line = getComputedStyle(item.querySelector('.star-set-image'), '::after'); return {width:line.width,height:line.height,border:line.borderRight,transform:line.transform}; })() : null
      }));
      const signature = JSON.stringify([label, data]);
      if (signature === optionsSignature) return;
      optionsSignature = signature;
      const focused = panelOptions.contains(document.activeElement) ? document.activeElement.getAttribute('aria-label') : null;
      const previousScroll = panelOptions.querySelector('.sembonia-mobile-panel-options__list')?.scrollLeft || 0;
      panelOptions.hidden = !items.length;
      panelOptions.replaceChildren();
      if (!items.length) return;
      const heading = document.createElement('p');
      heading.className = 'sembonia-mobile-panel-options__label';
      const optionName = row.querySelector('.swatch-label .swatch-option-name')?.textContent.trim() || label;
      const optionValue = row.querySelector('.swatch-label .swatch-variant-name')?.textContent.trim();
      heading.textContent = optionName;
      if (optionValue) {
        const value = document.createElement('span');
        value.className = 'sembonia-mobile-panel-options__value';
        value.textContent = ` ${optionValue}`;
        heading.append(value);
      }
      const list = document.createElement('div');
      list.className = 'sembonia-mobile-panel-options__list';
      data.forEach((option, index) => {
        const control = document.createElement('button');
        control.type = 'button';
        control.setAttribute('aria-label', option.label);
        control.setAttribute('aria-pressed', String(option.selected));
        control.disabled = option.disabled;
        control.dataset.soldOut = String(option.soldOut);
        if (option.stockLine) {
          control.style.setProperty('--stock-line-width', option.stockLine.width);
          control.style.setProperty('--stock-line-height', option.stockLine.height);
          control.style.setProperty('--stock-line-border', option.stockLine.border);
          control.style.setProperty('--stock-line-transform', option.stockLine.transform);
        }
        if (option.image && option.image !== 'none') {
          control.dataset.image = 'true';
          control.style.backgroundImage = option.image;
        } else control.textContent = option.label;
        // Resolve the current app node each time: variant updates can replace it.
        control.addEventListener('click', () => {
          const original = panelOptionSource()?.querySelectorAll('.swatch-view-item')[index];
          (original?.querySelector('.swatch-selector') || original)?.click();
          schedule();
        });
        list.append(control);
      });
      panelOptions.append(heading, list);
      list.scrollLeft = previousScroll;
      if (focused) [...list.children].find(control => control.getAttribute('aria-label') === focused)?.focus({preventScroll:true});
    }
    const summaryHomes = summaryNodes.map(node => {
      const marker = document.createComment('PDP summary original position');
      node.before(marker);
      return marker;
    });
    const colourScrollRows = new WeakSet();
    function syncColourScrollEdges() {
      const rows = root.querySelectorAll('.sembonia-mobile-panel-options[data-option="colour"] .sembonia-mobile-panel-options__list');
      rows.forEach(row => {
        // Horizontal overflow is native; vertical gestures must reach the page.
        if (row.hasAttribute('data-lenis-prevent')) row.removeAttribute('data-lenis-prevent');
        if (!row.hasAttribute('tabindex')) row.setAttribute('tabindex', '0');
        const updateEdges = () => {
          const left = row.scrollLeft > 1 ? '18px' : '0px';
          const right = row.scrollWidth - row.clientWidth - row.scrollLeft > 1 ? '18px' : '0px';
          if (row.style.getPropertyValue('--colour-fade-left') !== left) row.style.setProperty('--colour-fade-left', left);
          if (row.style.getPropertyValue('--colour-fade-right') !== right) row.style.setProperty('--colour-fade-right', right);
        };
        if (!colourScrollRows.has(row)) {
          colourScrollRows.add(row);
          row.addEventListener('scroll', updateEdges, {passive:true});
        }
        updateEdges();
      });
    }
    const update = () => {
      frame = null;
      // A drawer's fixed-body lock is not a PDP scroll gesture. Preserve both the
      // panel mode and its measurements until the original scroll is restored.
      if (window.semboniaScrollLock?.busy || window.semboniaScrollLock?.size) return;
      if (transitionActive && window.innerWidth < 810) return;
      sortSizeOptions(root);
      root.querySelectorAll('.swatch-label').forEach(label => {
        const name = label.querySelector('.swatch-option-name');
        const value = label.querySelector('.swatch-variant-name');
        if (name && /^(size|saiz)\s*:?$/i.test(name.textContent.trim())) {
          const sizeLabel = 'Size';
          if (name.textContent !== sizeLabel) name.textContent = sizeLabel;
          return;
        }
        if (!name || !/^colou?r\s*: ?$/i.test(name.textContent.trim() + (name.textContent.includes(':') ? '' : ':'))) return;
        if (name.textContent !== 'Colour:') name.textContent = 'Colour:';
        if (value) {
          const colour = value.textContent.replace(/^\s*:\s*/, '').trim();
          if (value.textContent !== colour) value.textContent = colour;
        }
      });
      const hasBothOptions = String(hasColourAndSize());
      if (root.dataset.hasColourSize !== hasBothOptions) root.dataset.hasColourSize = hasBothOptions;
      if (dotNav) {
        const items = Array.from(gallery.querySelectorAll('.product__media-item')).filter(item => item.getClientRects().length && item.getBoundingClientRect().width > 0);
        if (items.length !== dotItems.length || items.some((item, index) => item !== dotItems[index])) {
          dotItems = items;
          dotNav.style.setProperty('--gallery-dot-count', String(items.length));
          dotNav.replaceChildren(...items.map((item, index) => {
            const dot = document.createElement('button');
            dot.type = 'button'; dot.setAttribute('aria-label', `View image ${index + 1} of ${items.length}`);
            dot.addEventListener('click', () => {
              const reduced = mobileBaseline() || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
              if (window.innerWidth < 810) gallery.scrollTo({left:gallery.scrollLeft + item.getBoundingClientRect().left - gallery.getBoundingClientRect().left, behavior:reduced ? 'instant' : 'smooth'});
              else item.scrollIntoView({block:'start', behavior:reduced ? 'instant' : 'smooth'});
            });
            return dot;
          }));
        }
        const horizontal = window.innerWidth < 810;
        const anchor = horizontal ? gallery.getBoundingClientRect().left + gallery.clientWidth / 2 : window.innerHeight / 2;
        let nearest = 0, distance = Infinity;
        items.forEach((item, index) => { const rect = item.getBoundingClientRect(); const delta = Math.abs((horizontal ? rect.left + rect.width / 2 : rect.top + rect.height / 2) - anchor); if (delta < distance) { distance = delta; nearest = index; } });
        Array.from(dotNav.children).forEach((dot, index) => { const value = String(index === nearest); if (dot.getAttribute('aria-current') !== value) dot.setAttribute('aria-current', value); });
        dotNav.hidden = items.length < 2;
        galleryArrows.forEach(arrow => { arrow.hidden = items.length < 2; });
      }
      const button = root.querySelector('.product-form__buttons .product-form__submit');
      const label = button?.querySelector(':scope > span');
      const price = button?.querySelector('.sembonia-bag-price');
      const source = root.querySelector('[data-sembonia-bag-price]');
      if (button && !secondary) {
        secondary = document.createElement('button');
        secondary.type = 'button';
        secondary.className = 'product-form__submit sembonia-secondary-cta';
        secondary.hidden = true;
        secondary.append(document.createElement('span'), document.createElement('span'));
        secondary.lastElementChild.className = 'sembonia-bag-price';
        const spinner = button.querySelector('.loading__spinner');
        if (spinner) secondary.insertBefore(spinner.cloneNode(true), secondary.lastElementChild);
        secondary.addEventListener('click', () => {
          if (window.innerWidth < 810 && hasColourAndSize()) {
            revealSizeOptions();
            return;
          }
          const original = root.querySelector('.product-form__buttons .product-form__submit');
          if (original && !original.disabled && original.getAttribute('aria-disabled') !== 'true') original.click();
        });
        mobilePanel = document.createElement('div');
        mobilePanel.className = 'sembonia-mobile-product-panel';
        reveal = document.createElement('button');
        reveal.type = 'button';
        reveal.className = 'sembonia-mobile-product-panel__handle';
        reveal.setAttribute('aria-label', 'Expand product details');
        reveal.setAttribute('aria-expanded', 'false');
        if (infoWrapper?.id) reveal.setAttribute('aria-controls', infoWrapper.id);
        reveal.addEventListener('click', () => {
          const expanded = reveal.getAttribute('aria-expanded') === 'true';
          const stops = panelStops();
          movePanelTo(expanded ? stops.collapsed : stops.expanded);
        });
        panelOptions = document.createElement('div');
        panelOptions.className = 'sembonia-mobile-panel-options';
        panelOptions.hidden = true;
        mobilePanel.append(reveal, panelOptions, secondary);
        panelSlot = document.createElement('div');
        panelSlot.className = 'sembonia-mobile-panel-slot';
        panelSlot.append(mobilePanel);
        info.prepend(panelSlot);
      }
      if (mobilePanel) {
        const mobile = window.innerWidth < 810;
        summaryNodes.forEach((node, index) => {
          if (mobile && node.parentElement !== mobilePanel) mobilePanel.insertBefore(node, panelOptions);
          else if (!mobile && node.parentElement === mobilePanel) summaryHomes[index].after(node);
        });
        if (root.dataset.mobilePanel !== String(mobile)) root.dataset.mobilePanel = String(mobile);
        if (mobile) {
          syncPanelOptions();
          syncColourScrollEdges();
          // Measure the compact state once and on return, not the shorter inline
          // summary: changing the gallery height during scrolling causes jumps.
          if (!panelHeight || root.dataset.panelPosition !== 'inline') {
            secondary.hidden = false;
            panelHeight = Math.ceil(mobilePanel.getBoundingClientRect().height);
          }
          const height = `${panelHeight}px`;
          if (root.style.getPropertyValue('--pdp-panel-height') !== height) root.style.setProperty('--pdp-panel-height', height);
          const viewportHeight = window.visualViewport?.height || window.innerHeight;
          const stops = panelStops();
          // Keep the returning information in flow until it reaches the bottom.
          // Switching to fixed halfway inserts a blank slot during the animation.
          const position = transitionActive ? 'inline' : window.scrollY <= stops.collapsed + 2 ? 'fixed' : mobilePanelPosition(root.dataset.panelPosition, panelSlot.getBoundingClientRect().top, viewportHeight, panelHeight);
          const inline = position === 'inline';
          if (root.dataset.panelPosition !== position) root.dataset.panelPosition = position;
          secondary.hidden = inline;
          const header = parseFloat(getComputedStyle(root).getPropertyValue('--sembonia-header-height')) || 64;
          if (mediaWrapper) {
            const mediaHeight = `${Math.ceil(mediaWrapper.getBoundingClientRect().height)}px`;
            if (mediaSpace.style.getPropertyValue('--mobile-media-height') !== mediaHeight) mediaSpace.style.setProperty('--mobile-media-height', mediaHeight);
            const pinned = String(mediaSpace.getBoundingClientRect().top <= header + 1);
            const covered = String(infoWrapper.getBoundingClientRect().top <= header);
            if (root.dataset.galleryPinned !== pinned) root.dataset.galleryPinned = pinned;
            if (root.dataset.galleryCovered !== covered) root.dataset.galleryCovered = covered;
          }
          const expanded = infoWrapper.getBoundingClientRect().top <= header + 24;
          const expandedValue = String(expanded);
          const revealLabel = expanded ? 'Collapse product details' : 'Expand product details';
          if (reveal.getAttribute('aria-expanded') !== expandedValue) reveal.setAttribute('aria-expanded', expandedValue);
          if (reveal.getAttribute('aria-label') !== revealLabel) reveal.setAttribute('aria-label', revealLabel);
        } else {
          cancelPanelMotion(); clearPanelLayer(); gesture = null; resumePanelTarget = null;
          secondary.hidden = true;
          panelHeight = 0;
          delete root.dataset.panelPosition;
          delete root.dataset.galleryPinned;
          delete root.dataset.galleryCovered;
          root.style.removeProperty('--pdp-panel-height');
        }
      }
      if (button && label && price) {
        if (!button.disabled && button.getAttribute('aria-disabled') !== 'true' && !label.classList.contains('hidden') && label.textContent.trim() !== 'Add to bag') label.textContent = 'Add to bag';
        const value = source?.textContent.trim().replace(/RM\s+/g, 'RM');
        if (value && price.textContent !== value) price.textContent = value;
      }
      // The size action must not depend on the optional price span being present.
      if (button && secondary) {
        const pending = button.getAttribute('aria-busy') === 'true';
        if (secondary.getAttribute('aria-busy') !== String(pending)) secondary.setAttribute('aria-busy', String(pending));
        const selectSize = window.innerWidth < 810 && hasColourAndSize();
        const loading = !selectSize && (pending || button.classList.contains('loading'));
        secondary.classList.toggle('loading', loading);
        secondary.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
        if (secondary.dataset.selectSize !== String(selectSize)) secondary.dataset.selectSize = String(selectSize);
        const unavailable = !selectSize && (button.disabled || button.getAttribute('aria-disabled') === 'true');
        if (secondary.disabled !== unavailable) secondary.disabled = unavailable;
        const panelCtaLabel = selectSize ? 'Select size' : (label?.textContent || button.textContent.trim());
        if (secondary.firstElementChild.textContent !== panelCtaLabel) secondary.firstElementChild.textContent = panelCtaLabel;
        secondary.lastElementChild.hidden = selectSize;
        if (secondary.lastElementChild.textContent !== (price?.textContent || '')) secondary.lastElementChild.textContent = price?.textContent || '';
      }
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    const syncPanelBaseline = () => {
      if (mobileBaseline()) {
        cancelPanelMotion(); clearPanelLayer(); gesture = null;
        resumePanelTarget = null; resumeContentTarget = null;
      }
      schedule();
    };
    const observer = new MutationObserver(schedule);
    observer.observe(root, {subtree:true, childList:true, characterData:true, attributes:true, attributeFilter:['disabled','aria-disabled','aria-busy','aria-checked','aria-label','class']});
    window.addEventListener('scroll', onPanelScroll, {passive:true});
    window.addEventListener('sembonia:scroll-restored', onPanelRestored);
    window.addEventListener('sembonia:scroll-lock', onPanelLocked);
    window.addEventListener('sembonia:mobile-baseline-change', syncPanelBaseline);
    window.addEventListener('touchstart', onPanelTouchStart, {passive:true, capture:true});
    window.addEventListener('touchmove', onPanelTouchMove, {passive:false, capture:true});
    window.addEventListener('touchend', onPanelTouchEnd, {passive:true, capture:true});
    window.addEventListener('touchcancel', onPanelTouchCancel, {passive:true, capture:true});
    window.addEventListener('wheel', onPanelWheel, {passive:false, capture:true});
    gallery?.addEventListener('scroll', schedule, {passive:true});
    window.addEventListener('resize', schedule, {passive:true});
    const resize = new ResizeObserver(schedule);
    resize.observe(document.body);
    update();
    if (mobilePanel) resize.observe(mobilePanel);
    window.visualViewport?.addEventListener('resize', schedule);
    document.addEventListener('shopify:section:unload', function unload(event) {
      if (!event.target.contains(root)) return;
      dialogs.forEach(dialog => {
        dialog.close();
        window.semboniaScrollLock.release(dialog);
        dialog.remove();
      });
      dialogBaselineCleanups.forEach(cleanup => cleanup());
      summaryNodes.forEach((node, index) => { summaryHomes[index].after(node); summaryHomes[index].remove(); });
      panelSlot?.remove();
      mediaSpace.remove();
      dotNav?.remove();
      galleryArrows.forEach(arrow => arrow.remove());
      gallery?.removeEventListener('scroll', schedule);
      observer.disconnect(); resize.disconnect(); cancelAnimationFrame(frame);
      cancelPanelMotion();
      clearPanelLayer();
      window.removeEventListener('scroll', onPanelScroll); window.removeEventListener('resize', schedule);
      window.removeEventListener('sembonia:scroll-restored', onPanelRestored);
      window.removeEventListener('sembonia:scroll-lock', onPanelLocked);
      window.removeEventListener('sembonia:mobile-baseline-change', syncPanelBaseline);
      window.removeEventListener('touchstart', onPanelTouchStart, true);
      window.removeEventListener('touchmove', onPanelTouchMove, true);
      window.removeEventListener('touchend', onPanelTouchEnd, true);
      window.removeEventListener('touchcancel', onPanelTouchCancel, true);
      window.removeEventListener('wheel', onPanelWheel, true);
      window.visualViewport?.removeEventListener('resize', schedule);
      document.removeEventListener('shopify:section:unload', unload);
    });
  }
  const start = () => document.querySelectorAll('.sembonia-pdp-tune').forEach(mount);
  start();
  document.addEventListener('shopify:section:load', start);
})();
